import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, apiGet, apiPost } from "../api/client";
import { useMe } from "../hooks/useMe";

interface Preview {
  workspaceName: string;
  cxoRoleTag: string;
  invitedEmail: string;
  role: string;
  status: "open" | "consumed" | "expired";
  expiresAt: string;
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen grid place-items-center bg-bg-canvas p-6 font-geist">
      <div className="w-full max-w-md rounded-2xl border border-border bg-bg p-10 shadow-sm">
        {children}
      </div>
    </main>
  );
}

function InfoBlock({ tone, title, body }: { tone: "info" | "warn" | "danger"; title: string; body: string }) {
  const toneClass =
    tone === "danger"
      ? "bg-danger-soft text-danger"
      : tone === "warn"
        ? "bg-warn-soft text-warn"
        : "bg-accent-soft text-accent";
  return (
    <div className={`rounded-lg p-4 text-sm ${toneClass}`}>
      <p className="font-medium">{title}</p>
      <p className="mt-1 opacity-90">{body}</p>
    </div>
  );
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const previewQ = useQuery<Preview, Error>({
    queryKey: ["invite", token],
    queryFn: () => apiGet<Preview>(`/api/invites/${token}`),
    enabled: !!token,
    retry: false,
  });

  const me = useMe();

  const acceptM = useMutation({
    mutationFn: () => apiPost(`/api/invites/${token}/accept`, {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/post-login");
    },
  });

  if (!token) {
    return (
      <PageShell>
        <h1 className="text-xl font-semibold text-text">Invalid invite link</h1>
        <p className="mt-2 text-sm text-text-3">No token provided.</p>
      </PageShell>
    );
  }

  if (previewQ.isLoading) {
    return (
      <PageShell>
        <div className="h-4 w-24 animate-pulse rounded bg-bg-soft" />
        <div className="mt-4 h-7 w-56 animate-pulse rounded bg-bg-soft" />
        <div className="mt-2 h-4 w-40 animate-pulse rounded bg-bg-soft" />
        <div className="mt-8 h-10 w-full animate-pulse rounded bg-bg-soft" />
      </PageShell>
    );
  }

  if (previewQ.error) {
    const status = previewQ.error instanceof ApiError ? previewQ.error.status : 0;
    if (status === 404) {
      return (
        <PageShell>
          <h1 className="text-xl font-semibold text-text">Invite not found</h1>
          <p className="mt-2 text-sm text-text-3">This invite link is invalid.</p>
        </PageShell>
      );
    }
    if (status === 410) {
      return (
        <PageShell>
          <h1 className="text-xl font-semibold text-text">Invite no longer valid</h1>
          <p className="mt-2 text-sm text-text-3">
            This invite has expired or been used.
          </p>
        </PageShell>
      );
    }
    return (
      <PageShell>
        <h1 className="text-xl font-semibold text-text">Something went wrong</h1>
        <p className="mt-2 text-sm text-danger">{previewQ.error.message}</p>
      </PageShell>
    );
  }

  const preview = previewQ.data;
  if (!preview) return null;

  const isUnauth = me.error instanceof ApiError && me.error.status === 401;
  const isAuth = !!me.data;
  const emailMatch =
    isAuth && me.data!.user.email.toLowerCase() === preview.invitedEmail.toLowerCase();

  return (
    <PageShell>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-3">
        Workspace invitation
      </p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text">
        {preview.workspaceName}
      </h1>
      <p className="mt-1 text-sm text-text-2">
        <span className="text-accent">{preview.cxoRoleTag}</span> workspace
      </p>

      <dl className="mt-6 space-y-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-text-3">Invited email</dt>
          <dd className="font-medium text-text">{preview.invitedEmail}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-text-3">Role</dt>
          <dd className="font-medium text-text">{preview.role}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-text-3">Status</dt>
          <dd>
            <span className={`pill ${preview.status === "open" ? "green" : "gray"}`}>
              {preview.status}
            </span>
          </dd>
        </div>
      </dl>

      <div className="mt-8">
        {preview.status === "consumed" && (
          <InfoBlock
            tone="warn"
            title="This invite has been used"
            body="Ask your administrator to send a fresh one if you still need access."
          />
        )}
        {preview.status === "expired" && (
          <InfoBlock
            tone="warn"
            title="This invite has expired"
            body="Ask your administrator to send a fresh one."
          />
        )}

        {preview.status === "open" && isUnauth && (
          <button
            type="button"
            onClick={() => {
              const returnTo = encodeURIComponent(`/invites/${token}`);
              window.location.assign(`/api/auth/login?returnTo=${returnTo}`);
            }}
            className="inline-flex w-full items-center justify-center rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:opacity-90 transition"
          >
            Sign in to accept
          </button>
        )}

        {preview.status === "open" && isAuth && !emailMatch && (
          <InfoBlock
            tone="danger"
            title="Wrong account"
            body={`This invite is for ${preview.invitedEmail}; you're signed in as ${me.data!.user.email}.`}
          />
        )}

        {preview.status === "open" && isAuth && emailMatch && (
          <>
            <button
              type="button"
              onClick={() => acceptM.mutate()}
              disabled={acceptM.isPending}
              className="inline-flex w-full items-center justify-center rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:opacity-90 transition disabled:opacity-50"
            >
              {acceptM.isPending ? "Accepting…" : "Accept invite"}
            </button>
            {acceptM.error && (
              <p className="mt-3 text-sm text-danger">
                {acceptM.error instanceof Error ? acceptM.error.message : String(acceptM.error)}
              </p>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}
